import { useEffect, useId, useRef } from 'react';

interface AnalyticsTabsProps<T extends string> {
  label: string;
  items: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  panelId: string;
}

export default function AnalyticsTabs<T extends string>({ label, items, value, onChange, panelId }: AnalyticsTabsProps<T>) {
  const id = useId();
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const parent = list.current;
    if (!parent) return;
    const reveal = () => {
      const active = parent.querySelector<HTMLElement>('[aria-selected="true"]');
      if (!active) return;
      const tabBounds = active.getBoundingClientRect();
      const listBounds = parent.getBoundingClientRect();
      if (tabBounds.left < listBounds.left || tabBounds.right > listBounds.right) {
        parent.scrollLeft += tabBounds.left - listBounds.left - (parent.clientWidth - active.clientWidth) / 2;
      }
    };
    reveal();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(reveal);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [value]);
  return (
    <div ref={list} className="analytics-tabs" role="tablist" aria-label={label}>
      {items.map((item, index) => (
        <button
          key={item.id}
          id={`${id}-${item.id}`}
          type="button"
          role="tab"
          aria-selected={item.id === value}
          aria-controls={panelId}
          tabIndex={item.id === value ? 0 : -1}
          onClick={() => onChange(item.id)}
          onKeyDown={(event) => {
            const nextIndex = event.key === 'ArrowRight' ? (index + 1) % items.length
              : event.key === 'ArrowLeft' ? (index + items.length - 1) % items.length
                : event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : null;
            if (nextIndex === null) return;
            event.preventDefault();
            const next = items[nextIndex];
            onChange(next.id);
            document.getElementById(`${id}-${next.id}`)?.focus();
          }}
        >{item.label}</button>
      ))}
    </div>
  );
}
